import React, { useState, useRef, useEffect } from 'react';
import { COUNTRIES } from '../data/countries';

/**
 * CountrySelector - Searchable country dropdown
 * Props: value (country code), onChange(code), language, style
 */
export function CountrySelector({ value, onChange, language, style = {} }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = COUNTRIES.find(c => c.code === value) || COUNTRIES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = search
    ? COUNTRIES.filter(c => {
        const q = search.toLowerCase();
        return c.name_tr.toLowerCase().includes(q) || c.name_en.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      })
    : COUNTRIES;

  const S = {
    trigger: {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '10px 12px', width: '100%', cursor: 'pointer',
      border: '1.5px solid #D8D1C6', borderRadius: '10px',
      background: 'white', fontFamily: 'Manrope, sans-serif',
      fontSize: '14px', color: '#1F3A2E', textAlign: 'left',
      ...style,
    },
    dropdown: {
      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 9999,
      background: 'white', border: '1px solid #D8D1C6', borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden',
      maxHeight: '280px', display: 'flex', flexDirection: 'column',
    },
    searchInput: {
      padding: '10px 12px', border: 'none', borderBottom: '1px solid #EFE7DA',
      fontSize: '13px', fontFamily: 'Manrope, sans-serif', outline: 'none',
      background: '#F9F7F4', width: '100%', boxSizing: 'border-box',
    },
    list: { overflowY: 'auto', flex: 1 },
    item: (isSelected) => ({
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '9px 14px', cursor: 'pointer', fontSize: '13px',
      background: isSelected ? '#F0FAF0' : 'transparent',
      color: isSelected ? '#1F3A2E' : '#3A3632',
      fontFamily: 'Manrope, sans-serif', border: 'none', width: '100%', textAlign: 'left',
    }),
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button type="button" style={S.trigger} onClick={() => setOpen(!open)}>
        <span style={{ fontSize: '18px' }}>{selected.flag}</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {language === 'tr' ? selected.name_tr : selected.name_en}
        </span>
        <span style={{ color: '#6E7F5B', fontSize: '10px', flexShrink: 0 }}>▼</span>
      </button>
      {open && (
        <div style={S.dropdown}>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={language === 'tr' ? 'Ülke ara...' : 'Search country...'}
            style={S.searchInput}
          />
          <div style={S.list}>
            {filtered.length === 0 ? (
              <p style={{ padding: '12px', textAlign: 'center', color: '#B0A99A', fontSize: '13px' }}>
                {language === 'tr' ? 'Sonuç bulunamadı' : 'No results'}
              </p>
            ) : filtered.map(c => (
              <button
                key={c.code}
                type="button"
                style={S.item(c.code === value)}
                onClick={() => { onChange(c.code); setOpen(false); setSearch(''); }}
                onMouseEnter={e => { if (c.code !== value) e.currentTarget.style.background = '#F6F1E7'; }}
                onMouseLeave={e => { if (c.code !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '16px', flexShrink: 0 }}>{c.flag}</span>
                <span style={{ flex: 1 }}>{language === 'tr' ? c.name_tr : c.name_en}</span>
                {c.code === value && <span style={{ color: '#1F3A2E', fontSize: '12px' }}>✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * PhoneInput - Country dial code + phone number
 * Props: dialCode, phone, onDialCodeChange, onPhoneChange, language
 */
export function PhoneInput({ dialCode, phone, onDialCodeChange, onPhoneChange, language }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selectedCountry = COUNTRIES.find(c => c.dialCode === dialCode) || COUNTRIES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const filtered = search
    ? COUNTRIES.filter(c => {
        const q = search.toLowerCase();
        return c.name_tr.toLowerCase().includes(q) || c.name_en.toLowerCase().includes(q) || c.dialCode.includes(q);
      })
    : COUNTRIES;

  return (
    <div style={{ display: 'flex', gap: '0', border: '1.5px solid #D8D1C6', borderRadius: '10px', overflow: 'visible', background: 'white' }}>
      {/* Dial code selector */}
      <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            padding: '10px 10px', cursor: 'pointer', border: 'none',
            borderRight: '1px solid #EFE7DA', borderRadius: '9px 0 0 9px',
            background: '#F9F7F4', fontFamily: 'Manrope, sans-serif',
            fontSize: '13px', color: '#1F3A2E', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: '16px' }}>{selectedCountry.flag}</span>
          <span style={{ fontWeight: 600 }}>{dialCode}</span>
          <span style={{ fontSize: '9px', color: '#6E7F5B' }}>▼</span>
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
            background: 'white', border: '1px solid #D8D1C6', borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden',
            maxHeight: '260px', width: '220px', display: 'flex', flexDirection: 'column',
          }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={language === 'tr' ? 'Ülke ara...' : 'Search...'}
              style={{ padding: '8px 12px', border: 'none', borderBottom: '1px solid #EFE7DA', fontSize: '12px', fontFamily: 'Manrope, sans-serif', outline: 'none', background: '#F9F7F4', boxSizing: 'border-box' }}
            />
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filtered.map(c => (
                <button
                  key={c.code + c.dialCode}
                  type="button"
                  onClick={() => { onDialCodeChange(c.dialCode); setOpen(false); setSearch(''); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', border: 'none', cursor: 'pointer',
                    background: c.dialCode === dialCode && c.code === selectedCountry.code ? '#F0FAF0' : 'transparent',
                    fontSize: '12px', fontFamily: 'Manrope, sans-serif', textAlign: 'left', color: '#1F3A2E',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F6F1E7'}
                  onMouseLeave={e => e.currentTarget.style.background = c.dialCode === dialCode ? '#F0FAF0' : 'transparent'}
                >
                  <span style={{ fontSize: '14px' }}>{c.flag}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {language === 'tr' ? c.name_tr : c.name_en}
                  </span>
                  <span style={{ fontWeight: 700, color: '#6E7F5B', fontSize: '11px' }}>{c.dialCode}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Phone number input */}
      <input
        type="tel"
        value={phone}
        onChange={e => onPhoneChange(e.target.value)}
        placeholder={language === 'tr' ? '5XX XXX XX XX' : '5XX XXX XX XX'}
        data-testid="booking-form-phone-input"
        style={{
          flex: 1, padding: '10px 12px', border: 'none', outline: 'none',
          fontSize: '14px', fontFamily: 'Manrope, sans-serif', background: 'transparent',
          borderRadius: '0 9px 9px 0', minWidth: 0,
        }}
      />
    </div>
  );
}
